import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RecordContextProvider, useGetOne, usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useDrawer } from '@/composables/useDrawer';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import { RetailChainBasicDetails, RetailChainConfigurationDetails } from '@/components/retailChain/RetailChainDetails.jsx';

export const RetailChainShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();
    const { closeAllDrawers, openDrawer } = useDrawer();
    const allowed = canViewDetails('retail-chain', permissions);
    const { data, isPending, error } = useGetOne('retail-chain', { id: elementId }, { enabled: Boolean(elementId) && allowed && !isPermissionsLoading });
    const [isBasicOpen, setIsBasicOpen] = useState(true);
    const [isConfigurationOpen, setIsConfigurationOpen] = useState(false);
    const canEdit = Boolean(permissions?.isAdmin);
    const loadingState = getLoadingState({ isLoading: isPending, error, data, translate });

    useEffect(() => {
        if (!isPermissionsLoading && !allowed) {
            closeAllDrawers();
        }
    }, [allowed, closeAllDrawers, isPermissionsLoading]);

    if (isPermissionsLoading || !allowed) {
        return null;
    }

    return (
        <Box>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{data?.name || translate('resources.retailChain.labels.details')}</Typography>
                    <Box className='popup-box__more-actions-grey' sx={{ height: '44px' }}>
                        {canEdit && data?.id ? (
                            <CustomButton
                                color='primary'
                                label='common.edit'
                                variant='contained'
                                size={isDesktop ? 'large' : 'small'}
                                startIcon={<EditIcon />}
                                onClick={() => openDrawer(elementId, 'retail-chain-edit')}
                            />
                        ) : null}
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            {loadingState || (
                <Box className='popup-box'>
                    <RecordContextProvider value={data}>
                        <CollapsibleSection
                            title={translate('resources.retailChain.labels.basicInfo')}
                            isOpen={isBasicOpen}
                            onToggle={() => setIsBasicOpen(open => !open)}
                        >
                            <RetailChainBasicDetails />
                        </CollapsibleSection>
                        <CollapsibleSection
                            title={translate('resources.retailChain.labels.configuration')}
                            isOpen={isConfigurationOpen}
                            onToggle={() => setIsConfigurationOpen(open => !open)}
                        >
                            <RetailChainConfigurationDetails />
                        </CollapsibleSection>
                    </RecordContextProvider>
                </Box>
            )}
        </Box>
    );
};

RetailChainShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
